/**
 * Password policy constants derived from the ThingSpace credentials service
 * (credentials/policy/policy.go + credentials/service/policy.json).
 *
 * API-enforced rules (applied at creation & reset time via ValidatePassword()):
 *   - Minimum 8 characters
 *   - At least one uppercase letter (A-Z)
 *   - At least one lowercase letter (a-z)
 *   - At least one digit (0-9)
 *   - At least one special character from the 32 allowed:
 *       ! " # $ % & ' ( ) * + , - . / : ; < = > ? @
 *       [ \ ] ^ _ ` { | } ~
 *   - No more than 2 consecutive identical characters (e.g. "aaa" is rejected)
 *   - No 3 or more sequential characters ascending or descending
 *       (e.g. "abc", "123", "321" are rejected)
 *   - Cannot reuse any of the last 5 passwords
 *   - Cannot reuse a password used in the last 12 months
 *
 * Note: At login time the API backend performs NO character-format validation —
 * it only bcrypt-compares the hash. The regex below is therefore the sole
 * client-side gate at login, so it must match exactly what creation allows.
 */

const _BASE_REGEX =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!"#$%&'()*+,\-./:;<=>?@\[\\\]^_`{|}~])[A-Za-z\d!"#$%&'()*+,\-./:;<=>?@\[\\\]^_`{|}~]{8,}$/;

function _hasTripleRepeat(pwd) {
  // "aaa", "111", etc.
  return /(.)\1\1/.test(pwd);
}

function _hasSequentialChars(pwd) {
  // 3 or more ascending or descending consecutive code points
  for (let i = 0; i < pwd.length - 2; i++) {
    const a = pwd.charCodeAt(i);
    const b = pwd.charCodeAt(i + 1);
    const c = pwd.charCodeAt(i + 2);
    if (b - a === 1 && c - b === 1) return true; // ascending: abc, 123
    if (a - b === 1 && b - c === 1) return true; // descending: cba, 321
  }
  return false;
}

export const validatePassword = (pwd) =>
  _BASE_REGEX.test(pwd) && !_hasTripleRepeat(pwd) && !_hasSequentialChars(pwd);

// Keep for callers that still import PASSWORD_REGEX directly
export const PASSWORD_REGEX = _BASE_REGEX;

export const PASSWORD_RULES = [
  "At least 8 characters",
  "One uppercase letter",
  "One lowercase letter",
  "One number",
  "One special character",
  "No more than 2 identical characters in a row (e.g. \"aaa\" is not allowed)",
  "No 3 or more sequential characters (e.g. \"abc\", \"123\", \"321\" are not allowed)",
  "Cannot be the same as your last 5 passwords or any password used in the past 12 months",
];
